// UK-specific service landing configs. These drive the /uk/* sub-routes
// (e.g. /uk/software-development-company) and reuse the same premium
// <ServicePage /> template with UK-focused hero copy, evidence and stats.

export const UK_SERVICE_CONFIGS = {
  "software-development-company": {
    slug: "software-development-company",
    parent: "custom-software-development",
    name: "UK Software Development Company",
    kicker: "United Kingdom · Software · Web · Custom platforms",
    hero: {
      title: "The software development company",
      highlight: "British businesses trust.",
      subtitle:
        "CloudLab is a UK-headquartered software development company building bespoke web platforms, SaaS products and enterprise systems for British businesses — from Glasgow to London — with a Scotland head office and a senior engineering pod behind every account.",
      stats: [
        { v: "Glasgow", l: "UK head office" },
        { v: "GBP", l: "Invoiced locally" },
        { v: "GMT", l: "Time-zone aligned" },
        { v: "12+ yrs", l: "Delivering software" },
      ],
    },
    hero_image:
      "https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "What we build for UK clients",
      title: "SaaS, enterprise systems and everything between.",
      subtitle:
        "From growth-stage SaaS in London to universities in Glasgow and Manchester — we build software that runs the business, not slideware.",
      items: [
        { icon: "Globe", title: "Web & SaaS Platforms", desc: "SEO-first React / Next.js platforms shipped every two weeks, hosted in UK-region cloud." },
        { icon: "Server", title: "Enterprise Systems", desc: "Bespoke ERPs, CRMs and back-office tooling for mid-market and enterprise UK clients." },
        { icon: "ShoppingBag", title: "E-Commerce", desc: "Shopify Plus and headless commerce for UK D2C brands — multi-currency, VAT-aware." },
        { icon: "Database", title: "APIs & Integrations", desc: "GraphQL / REST middleware connecting Salesforce, Xero, HubSpot, Sage, HMRC and more." },
        { icon: "ShieldCheck", title: "GDPR + Security", desc: "GDPR-first architecture, ICO-friendly DPAs, encryption at rest and audit-ready logs." },
        { icon: "Cpu", title: "AI Integration", desc: "LLM copilots and AI features embedded in your existing platforms — OpenAI, Claude, Gemini." },
      ],
    },
    tech: {
      kicker: "Stack we ship in the UK",
      title: "Modern stacks, cloud-native, GDPR-conscious.",
      items: [
        { name: "React" }, { name: "Next.js" }, { name: "Node.js" }, { name: "TypeScript" },
        { name: "PostgreSQL" }, { name: "MongoDB" }, { name: "AWS eu-west-2" }, { name: "Azure UK South" },
        { name: "Cloudflare" }, { name: "GitHub Actions" }, { name: "OpenAI" }, { name: "Stripe" },
      ],
    },
    steps: {
      kicker: "How we work with UK clients",
      title: "A delivery model that respects British working hours.",
      items: [
        { title: "Discover", desc: "Blueprint workflow, KPIs and integrations in a two-week paid discovery." },
        { title: "Design", desc: "Clickable prototype signed off before we write code — measure twice, cut once." },
        { title: "Build", desc: "Two-week sprints, weekly demos on Teams / Meet, continuous integration and testing." },
        { title: "Launch & support", desc: "Zero-downtime deploy, plus a defined SLA-backed support plan for post-launch." },
      ],
    },
    outcomes: [
      "GDPR-compliant, UK-region hosted from day one",
      "GBP invoicing via our UK Ltd entity",
      "Time-zone aligned team on GMT / BST",
      "Full source, IP and infrastructure ownership",
    ],
    ctas: { primary: { label: "Start a UK project", to: "/quote" } },
  },

  "crm-development": {
    slug: "crm-development",
    parent: "crm-development",
    name: "UK CRM Development",
    kicker: "United Kingdom · Custom CRM · AI copilots",
    hero: {
      title: "Custom CRM development for",
      highlight: "British businesses.",
      subtitle:
        "Bespoke sales, service, admissions and field-force CRMs — built around how your UK team actually works, invoiced in GBP, hosted in UK-region cloud with GDPR at the core.",
      stats: [
        { v: "60+", l: "CRM deployments" },
        { v: "GBP", l: "Local invoicing" },
        { v: "12wk", l: "Typical go-live" },
        { v: "AI", l: "Copilot ready" },
      ],
    },
    hero_image:
      "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "CRMs we build for UK teams",
      title: "Sales, admissions, service, field-force — done properly.",
      items: [
        { icon: "Users", title: "Sales CRM", desc: "Pipeline, forecasting, quoting and territory management for B2B UK sales teams." },
        { icon: "GraduationCap", title: "Admissions CRM", desc: "UCAS-aware admissions funnels for UK universities, colleges and education agents." },
        { icon: "Headphones", title: "Service CRM", desc: "Ticketing, SLAs, knowledge base and customer 360 for UK service teams." },
        { icon: "Truck", title: "Field-force CRM", desc: "Route planning, offline visits, geo-check-ins for UK outside sales reps." },
        { icon: "MessageCircle", title: "WhatsApp + Voice", desc: "Native WhatsApp Business + Twilio voice capture — every UK lead in one inbox." },
        { icon: "Bot", title: "AI Copilots", desc: "Auto-summaries, next-best-action and voice-to-CRM logging for UK teams." },
      ],
    },
    tech: {
      kicker: "Stack",
      items: [
        { name: "React" }, { name: "Next.js" }, { name: "Node.js" }, { name: "TypeScript" },
        { name: "PostgreSQL" }, { name: "WhatsApp API" }, { name: "Twilio" }, { name: "OpenAI" },
        { name: "AWS eu-west-2" }, { name: "Azure UK South" }, { name: "Stripe" }, { name: "Xero" },
      ],
    },
    steps: {
      kicker: "How we deliver in the UK",
      title: "From process audit to live CRM in 12 weeks.",
      items: [
        { title: "Process audit", desc: "Map every stage, hand-off and integration inside a two-week UK-based discovery." },
        { title: "Design", desc: "Clickable prototype signed off before build." },
        { title: "Build", desc: "Two-week sprints. Live sandbox from week 3. UK users testing from week 6." },
        { title: "Migrate & train", desc: "Data migration from Salesforce / HubSpot / spreadsheets, role training, SLA-backed go-live." },
      ],
    },
    outcomes: [
      "GDPR-compliant, UK-region hosted",
      "GBP invoicing, UK Ltd contracting entity",
      "75%+ adoption vs 30% Salesforce norm",
      "Full ownership of code, data & workflow",
    ],
    ctas: { primary: { label: "Scope your UK CRM", to: "/quote" } },
  },

  "mobile-app-development": {
    slug: "mobile-app-development",
    parent: "mobile-app-development",
    name: "UK Mobile App Development",
    kicker: "United Kingdom · iOS · Android · Cross-platform",
    hero: {
      title: "Mobile app development for",
      highlight: "British brands & businesses.",
      subtitle:
        "Native-quality iOS and Android apps engineered for UK consumers, enterprises and field teams — from Glasgow to London — with App Store / Play Store submission, GDPR compliance and ongoing support included.",
      stats: [
        { v: "50+", l: "Apps published" },
        { v: "iOS +", l: "Android native" },
        { v: "GBP", l: "Local invoicing" },
        { v: "4.7★", l: "Avg. App Store rating" },
      ],
    },
    hero_image:
      "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "Apps we build for UK clients",
      title: "Every kind of mobile experience — done properly.",
      items: [
        { icon: "Apple", title: "iOS Native", desc: "Swift / SwiftUI apps built with the latest iOS SDK and Apple design guidelines." },
        { icon: "Smartphone", title: "Android Native", desc: "Kotlin Jetpack apps ready for Play Store — Material 3, adaptive layouts." },
        { icon: "Layers", title: "Cross-platform", desc: "React Native + Flutter shipping to iOS and Android from one codebase." },
        { icon: "ShoppingBag", title: "M-Commerce", desc: "Apps for UK retail brands — Apple Pay, Google Pay, biometrics." },
        { icon: "Users", title: "Field-force", desc: "Rugged offline-first apps for UK sales, service and delivery teams." },
        { icon: "Cloud", title: "Enterprise", desc: "Companion apps for UK SaaS — secure, SSO-ready, MDM-friendly." },
      ],
    },
    tech: {
      kicker: "Stack",
      items: [
        { name: "Swift" }, { name: "Kotlin" }, { name: "React Native" }, { name: "Flutter" },
        { name: "Firebase" }, { name: "Expo" }, { name: "AppCenter" }, { name: "TestFlight" },
        { name: "Apple Pay" }, { name: "Google Pay" }, { name: "OneSignal" }, { name: "Sentry" },
      ],
    },
    steps: {
      kicker: "How we work with UK clients",
      title: "From spec to App Store in weeks.",
      items: [
        { title: "Scope", desc: "Feature map, monetisation and delivery timeline defined in a UK-based discovery." },
        { title: "Design", desc: "Native UI systems for iOS + Android with clickable prototypes." },
        { title: "Build", desc: "Weekly TestFlight / Play builds — real UK users, real feedback." },
        { title: "Launch", desc: "Store submission, ASO, and post-launch monitoring + iteration." },
      ],
    },
    outcomes: [
      "Native performance on iOS + Android",
      "GDPR + App Tracking Transparency compliant",
      "GBP invoicing, UK Ltd contracting entity",
      "App Store & Play Store submission handled",
    ],
    ctas: { primary: { label: "Start a UK mobile project", to: "/quote" } },
  },

  "ai-development": {
    slug: "ai-development",
    parent: "ai-development",
    name: "UK AI Development Company",
    kicker: "United Kingdom · AI · LLMs · Voice · Data",
    hero: {
      title: "The AI development company",
      highlight: "British enterprises pick.",
      subtitle:
        "CloudLab builds enterprise-grade AI systems for UK businesses — LLM copilots grounded in your data, voice agents on UK phone numbers, document AI and computer vision — with GDPR-first design and UK-region hosting.",
      stats: [
        { v: "GPT-5", l: "+ Claude + Gemini" },
        { v: "40M+", l: "AI interactions served" },
        { v: "GDPR", l: "First" },
        { v: "24×7", l: "Autonomous operation" },
      ],
    },
    hero_image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1200&q=80",
    capabilities: {
      kicker: "AI we build for UK clients",
      title: "Every kind of AI system your UK business needs.",
      items: [
        { icon: "Sparkles", title: "LLM Copilots", desc: "GPT / Claude / Gemini copilots grounded in your docs, data and workflows — deployed in your UK VPC." },
        { icon: "Bot", title: "AI Voice Agents", desc: "24/7 AI voice agents on UK phone numbers — SDR, support, receptionist — Ofcom-friendly." },
        { icon: "MessageCircle", title: "AI Chat + WhatsApp", desc: "Website chat, WhatsApp Business and email — one AI agent trained on your UK operation." },
        { icon: "FileSearch", title: "Document AI", desc: "OCR + LLMs for invoices, KYC, HMRC forms, medical records — 99%+ accuracy." },
        { icon: "Eye", title: "Computer Vision", desc: "Quality-control, safety and CCTV vision systems at UK-scale." },
        { icon: "LineChart", title: "Predictive Models", desc: "Forecasting, scoring and recommendation models on your UK data." },
      ],
    },
    tech: {
      kicker: "Stack",
      items: [
        { name: "OpenAI" }, { name: "Anthropic Claude" }, { name: "Gemini" }, { name: "LangChain" },
        { name: "Python" }, { name: "PyTorch" }, { name: "Pinecone" }, { name: "Whisper" },
        { name: "AWS eu-west-2" }, { name: "Azure UK South" }, { name: "Twilio" }, { name: "Retell" },
      ],
    },
    steps: {
      kicker: "How we ship AI in the UK",
      title: "AI that goes live — not stays in a demo.",
      items: [
        { title: "Frame", desc: "Identify a valuable, measurable AI use-case tied to a real UK business metric." },
        { title: "Prototype", desc: "A working POC on your data in two weeks — decide with evidence." },
        { title: "Productionise", desc: "Guardrails, monitoring, feedback loops, GDPR + cost controls." },
        { title: "Improve", desc: "Continuous eval and model updates as your UK data grows." },
      ],
    },
    outcomes: [
      "GDPR-first, UK-region hosted AI",
      "Grounded, guardrailed LLM systems (no hallucinations)",
      "GBP invoicing, UK Ltd contracting entity",
      "Full ownership of prompts, models and pipelines",
    ],
    ctas: { primary: { label: "Start a UK AI project", to: "/quote" } },
  },
};

export const UK_SERVICE_KEYWORDS = {
  "software-development-company":
    "software development company UK, custom software development UK, bespoke software UK, software house Glasgow, software house London, UK software developers, GDPR software development, UK-region cloud development",
  "crm-development":
    "CRM development UK, custom CRM development UK, bespoke CRM UK, admissions CRM UK, sales CRM UK, HubSpot alternative UK, Salesforce alternative UK, UK CRM developers",
  "mobile-app-development":
    "mobile app development UK, iOS app development London, Android app development UK, React Native developers UK, mobile app agency Glasgow, mobile app agency London, UK mobile app studio",
  "ai-development":
    "AI development company UK, AI consultancy UK, AI voice agent UK, LLM development UK, ChatGPT integration UK, GDPR AI UK, UK AI engineers, AI development London, AI development Glasgow",
};
